<template>
	<div
		v-if="active"
		ref="message"
		:class="[
			`message message--${state} message--${size} message--${closeableStatus}`,
			{
				'is-animating-open' : animatingOpen,
				'is-animating-close' : animatingClose
			}
		]"
		role="status"
		:style="{
			'--max-height': maxHeight,
		}"
		data-cy="message"
	>
		<div :class="`message__bar message__bar--${state}`" />

		<div
			v-if="closeable"
			class="message__close"
			role="button"
			tabindex="0"
			:aria-label="$t('aria-label.close-message')"
			@click="animateClose"
			@keyup.space="animateClose"
			@keyup.enter="animateClose"
		>
			<helio-icon
				color="sky-gray7"
				token="times"
				weight="light"
			/>
		</div>

		<div class="message__content">
			<div
				v-if="state !== 'normal'"
				class="message__icon-container"
			>
				<div class="message__icon">
					<helio-icon
						:token="iconTokenWithFallback"
						:weight="compiledIconWeight"
					/>
				</div>
			</div>

			<div class="message__text">
				<div
					v-if="title"
					class="message__title"
				>
					{{ title }}
				</div>

				<slot name="content" />
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '../icon/Icon.vue';

export default {
	name: 'Message',

	components: {
		'helio-icon': Icon,
	},

	props: {
		active: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the Message will be displayed',
		},

		closeable: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the Message can be closed by a user',
		},

		iconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether the Message will have an icon and which icon will be displayed',
		},

		iconWeight: {
			type: String,
			required: false,
			default: null,
			description: 'If set, will override default internal component weight with this weight',
		},

		state: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'alert',
				'danger',
				'info',
				'normal',
				'success',
			].indexOf(value) !== -1,
			description: 'Sets the state of the Message',
		},

		title: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Message\'s title',
		},
	},

	slots: {
		content: {
			required: false,
			scoped: true,
			description: 'Supplies the main content of the Message component',
		},
	},

	data() {
		return {
			size: 'medium',
			animatingClose: false,
			animatingOpen: false,
			maxHeight: null,
		};
	},

	computed: {
		closeableStatus() {
			return this.closeable ? 'closeable' : 'not-closeable';
		},

		hasContentSlot() {
			return !!this.$slots.content || !!this.$scopedSlots.content;
		},

		iconTokenWithFallback() {
			if (!this.iconToken) {
				switch (this.state) {
					case 'danger':
						return 'exclamation-circle';

					case 'info':
						return 'info-circle';

					case 'success':
						return 'check-circle';

					case 'alert':
						return 'exclamation-triangle';

					default:
						break;
				}
			}

			return this.iconToken;
		},

		compiledIconWeight() {
			if (this.iconWeight) {
				return this.iconWeight;
			}

			return this.size === 'small' ? 'light' : 'bold';
		},
	},

	watch: {
		active() {
			this.onResize();
			this.animateOpen();
		},
	},

	mounted() {
		this.onResize();
		window.addEventListener('resize', this.onResize);
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			this.maxHeight = null;

			this.$nextTick(() => {
				const { message } = this.$refs;
				if (message) {
					this.size = message.clientWidth < 360 ? 'small' : 'medium';
				}
			});

			setTimeout(() => {
				const { message } = this.$refs;
				if (message) {
					this.maxHeight = `${message.clientHeight + 2}px`;
				}
			}, 600);
		},

		animateOpen() {
			this.animatingOpen = true;
			setTimeout(() => {
				this.animatingOpen = false;
			}, 500);
		},

		animateClose() {
			this.animatingClose = true;
			setTimeout(() => {
				this.animatingClose = false;
				this.$emit('close');
			}, 500);
		},
	},
};
</script>

<style lang="scss" src="./message.scss" />
